import { useCallback, useEffect, useState } from "react";
import { MdVisibility } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  InfoBox,
  Spinner,
  Text,
  VStack,
} from "components/ui";
import {
  DialogActionTrigger,
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
} from "components/ui/dialog";
import { useTranslation } from "hooks/useTranslation";
import {
  AiTraceAttempt,
  AiTraceCall,
  AiTraceDetail,
  aiGatewayService,
} from "services/aiGatewayService";
import { getThemeColor } from "theme/colors";

interface TraceDetailModalProps {
  /** `null` closes the dialog. Opening with a new id refetches — the detail is never cached. */
  batchId: string | null;
  onClose: () => void;
}

const formatDateTime = (iso: string) => new Date(iso).toLocaleString();

const formatUsd = (value: number) => {
  if (value === 0) return "$0.00";
  if (Math.abs(value) < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDuration = (ms: number | null) =>
  ms === null ? "—" : `${ms.toLocaleString()} ms`;

/**
 * One user turn, call by call and attempt by attempt.
 *
 * The three levels are `batchId` (the turn) → `traceId` (one logical call, e.g. the guardrail pass
 * or the main answer) → `attemptNumber` (each physical provider call, so a retry or a fallback adds
 * one). Only the last attempt of a call is marked terminal, and it is derived rather than stored —
 * which is why a call that succeeded on its second try still shows the first, failed one.
 */
const TraceDetailModal = ({ batchId, onClose }: TraceDetailModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<AiTraceDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    try {
      setDetail(await aiGatewayService.getTrace(id));
    } catch {
      // The interceptor toasts it; the dialog falls back to its empty state.
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setDetail(null);
    if (batchId) load(batchId);
  }, [batchId, load]);

  const callTitle = (call: AiTraceCall) =>
    call.subAgentLabel
      ? `${call.agentRoute ?? t("app.aiGateway.traces.detail.unknownRoute")} · ${call.subAgentLabel}`
      : (call.agentRoute ?? t("app.aiGateway.traces.detail.unknownRoute"));

  const renderAttempt = (attempt: AiTraceAttempt) => (
    <Box
      key={attempt.id}
      borderWidth="1px"
      borderRadius="md"
      borderColor="var(--border-default)"
      p={3}
    >
      <VStack align="stretch" gap={2}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
          <HStack gap={2} wrap="wrap">
            <Badge colorPalette="gray" variant="subtle">
              {t("app.aiGateway.traces.detail.attempt", {
                number: attempt.attemptNumber,
              })}
            </Badge>
            <Badge
              colorPalette={attempt.isSuccess ? "green" : "red"}
              variant="subtle"
            >
              {attempt.isSuccess
                ? t("app.aiGateway.traces.detail.success")
                : t("app.aiGateway.traces.detail.failed")}
            </Badge>
            {attempt.isTerminal && (
              <Badge colorPalette="blue" variant="subtle">
                {t("app.aiGateway.traces.detail.terminal")}
              </Badge>
            )}
            {attempt.httpStatusCode !== null && (
              <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                HTTP {attempt.httpStatusCode}
              </Text>
            )}
          </HStack>
          <Text fontSize="xs" color={getThemeColor("text.secondary")}>
            {formatDateTime(attempt.executedAt)}
          </Text>
        </Flex>

        <Flex gap={4} wrap="wrap">
          <VStack align="start" gap={0}>
            <Text fontSize="xs" color={getThemeColor("text.secondary")}>
              {t("app.aiGateway.traces.detail.model")}
            </Text>
            <Text fontSize="sm">
              {attempt.llmModel} · {attempt.llmProvider}
            </Text>
          </VStack>
          <VStack align="start" gap={0}>
            <Text fontSize="xs" color={getThemeColor("text.secondary")}>
              {t("app.aiGateway.traces.detail.resolvedBy")}
            </Text>
            <Text fontSize="sm">
              {attempt.resolvedBy ??
                t("app.aiGateway.traces.detail.unknownSource")}
            </Text>
          </VStack>
          <VStack align="start" gap={0}>
            <Text fontSize="xs" color={getThemeColor("text.secondary")}>
              {t("app.aiGateway.traces.detail.duration")}
            </Text>
            <Text fontSize="sm">{formatDuration(attempt.durationMs)}</Text>
          </VStack>
          <VStack align="start" gap={0}>
            <Text fontSize="xs" color={getThemeColor("text.secondary")}>
              {t("app.aiGateway.traces.detail.tokens")}
            </Text>
            <Text fontSize="sm">
              {t("app.aiGateway.traces.detail.tokensValue", {
                input: attempt.inputTokens?.toLocaleString() ?? "—",
                output: attempt.outputTokens?.toLocaleString() ?? "—",
              })}
            </Text>
          </VStack>
          <VStack align="start" gap={0}>
            <Text fontSize="xs" color={getThemeColor("text.secondary")}>
              {t("app.aiGateway.traces.detail.cost")}
            </Text>
            <Text fontSize="sm">
              {attempt.estimatedCostUsd === null
                ? t("app.aiGateway.traces.detail.notPriced")
                : formatUsd(attempt.estimatedCostUsd)}
            </Text>
          </VStack>
        </Flex>

        {attempt.errorMessage && (
          <Text fontSize="xs" color="red.500">
            {attempt.errorMessage}
          </Text>
        )}

        <HStack justify="flex-end">
          <Button
            intent="ghost"
            size="xs"
            onClick={() => {
              onClose();
              navigate(`/admin/ai-logs/${attempt.id}`);
            }}
          >
            <MdVisibility />
            {t("app.aiGateway.traces.detail.viewCall")}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );

  const renderCall = (call: AiTraceCall) => (
    <VStack key={call.traceId} align="stretch" gap={2}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
        <HStack gap={2} wrap="wrap">
          <Text fontSize="sm" fontWeight="semibold">
            {callTitle(call)}
          </Text>
          <Badge
            colorPalette={call.isSuccess ? "green" : "red"}
            variant="subtle"
          >
            {call.isSuccess
              ? t("app.aiGateway.traces.detail.success")
              : t("app.aiGateway.traces.detail.failed")}
          </Badge>
          {!call.hasTraceSpine && (
            <Badge colorPalette="orange" variant="subtle">
              {t("app.aiGateway.traces.detail.noSpine")}
            </Badge>
          )}
        </HStack>
        <Text fontSize="xs" color={getThemeColor("text.secondary")}>
          {t("app.aiGateway.traces.detail.callSummary", {
            count: call.attemptCount,
            cost: formatUsd(call.costUsd),
          })}
        </Text>
      </Flex>
      <VStack align="stretch" gap={2} pl={{ base: 0, md: 3 }}>
        {call.attempts.map(renderAttempt)}
      </VStack>
    </VStack>
  );

  return (
    <DialogRoot
      open={batchId !== null}
      onOpenChange={(e: { open: boolean }) => !e.open && onClose()}
      size="xl"
      scrollBehavior="inside"
    >
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("app.aiGateway.traces.detail.title")}</DialogTitle>
          </DialogHeader>

          <DialogBody>
            {loading && (
              <Flex justify="center" py={10}>
                <Spinner />
              </Flex>
            )}

            {!loading && !detail && (
              <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                {t("app.aiGateway.traces.detail.notFound")}
              </Text>
            )}

            {!loading && detail && (
              <VStack align="stretch" gap={4}>
                <Flex gap={4} wrap="wrap">
                  <VStack align="start" gap={0}>
                    <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                      {t("app.aiGateway.traces.detail.startedAt")}
                    </Text>
                    <Text fontSize="sm">
                      {formatDateTime(detail.startedAt)}
                    </Text>
                  </VStack>
                  <VStack align="start" gap={0}>
                    <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                      {t("app.aiGateway.traces.detail.totalCost")}
                    </Text>
                    <Text fontSize="sm">{formatUsd(detail.costUsd)}</Text>
                  </VStack>
                  <VStack align="start" gap={0}>
                    <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                      {t("app.aiGateway.traces.detail.tokens")}
                    </Text>
                    <Text fontSize="sm">
                      {t("app.aiGateway.traces.detail.tokensValue", {
                        input: detail.inputTokens.toLocaleString(),
                        output: detail.outputTokens.toLocaleString(),
                      })}
                    </Text>
                  </VStack>
                </Flex>

                {detail.unpricedCalls > 0 && (
                  <InfoBox variant="warning">
                    <Text fontSize="sm">
                      {t("app.aiGateway.traces.detail.unpricedNotice", {
                        count: detail.unpricedCalls,
                      })}
                    </Text>
                  </InfoBox>
                )}

                {detail.calls.length === 0 ? (
                  <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                    {t("app.aiGateway.traces.detail.noCalls")}
                  </Text>
                ) : (
                  detail.calls.map(renderCall)
                )}
              </VStack>
            )}
          </DialogBody>

          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button intent="secondary" size="sm" onClick={onClose}>
                {t("app.common.close")}
              </Button>
            </DialogActionTrigger>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  );
};

export default TraceDetailModal;
